import { AnyAction } from "redux";
import { ThunkAction } from "redux-thunk";
import { action } from "typesafe-actions";
import { api } from "../../api/api";
import { IQueryParams } from "../common/common.types";
import { getSearchQuery } from "../common/helpers";
import { IStoreState } from "..";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import { IInternalStoreTransfer } from "./internal-store-transfer.types";
import { server_base_endpoints } from "../../api/server-base-endpoints";

export const FETCH_INTERNAL_STORE_TRANSFER_LIST_PROGRESS =
  "FETCH_INTERNAL_STORE_TRANSFER_LIST_PROGRESS";
export const FETCH_INTERNAL_STORE_TRANSFER_LIST_SUCCESS =
  "FETCH_INTERNAL_STORE_TRANSFER_LIST_SUCCESS";
export const FETCH_INTERNAL_STORE_TRANSFER_LIST_FAILED =
  "FETCH_INTERNAL_STORE_TRANSFER_LIST_FAILED";

export const fetchInternalStoreTransferListProgress = () =>
  action(FETCH_INTERNAL_STORE_TRANSFER_LIST_PROGRESS);
export const fetchInternalStoreTransferListSuccess = (
  data: IInternalStoreTransfer[],
  totalRecords: number,
) => action(FETCH_INTERNAL_STORE_TRANSFER_LIST_SUCCESS, { data, totalRecords });
export const fetchInternalStoreTransferListFailed = () =>
  action(FETCH_INTERNAL_STORE_TRANSFER_LIST_FAILED);

export const fetchInternalStoreTransferListAsync =
  (queryParams: IQueryParams): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const searchQuery = getSearchQuery(queryParams);
      dispatch(fetchInternalStoreTransferListProgress());
      let finalUrl = `${server_base_endpoints.grn.get_internal_store_transfer}${searchQuery}`;

      const res = await api.get(finalUrl);
      const data: IInternalStoreTransfer[] = res.data.data;
      const totalRecords = res.data.totalRecords;
      dispatch(fetchInternalStoreTransferListSuccess(data, totalRecords));
    } catch (err: any) {
      dispatch(fetchInternalStoreTransferListFailed());
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message ||
            "Failed to fetch Internal Store Transfer list",
          displayAs: "snackbar",
        }),
      );
    }
  };

export const upsertSingleInternalStoreTransferAsync =
  (
    payload: IInternalStoreTransfer,
    onCallback: (
      isSuccess: boolean,
      internalStoreTransfer?: IInternalStoreTransfer,
    ) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const res = await api.post(
        `${server_base_endpoints.grn.upsert_internal_store_transfer}`,
        payload,
      );
      const data = res.data.data;
      onCallback(true, data);
      dispatch(
        showMessage({
          type: "success",
          message: "Internal Store Transfer saved successfully!",
          displayAs: "snackbar",
        }),
      );
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message ||
            "Failed to save Internal Store Transfer",
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const FETCH_SINGLE_INTERNAL_STORE_TRANSFER_PROGRESS =
  "FETCH_SINGLE_INTERNAL_STORE_TRANSFER_PROGRESS";
export const FETCH_SINGLE_INTERNAL_STORE_TRANSFER_SUCCESS =
  "FETCH_SINGLE_INTERNAL_STORE_TRANSFER_SUCCESS";
export const FETCH_SINGLE_INTERNAL_STORE_TRANSFER_FAILED =
  "FETCH_SINGLE_INTERNAL_STORE_TRANSFER_FAILED";

export const fetchSingleInternalStoreTransferProgress = () =>
  action(FETCH_SINGLE_INTERNAL_STORE_TRANSFER_PROGRESS);
export const fetchSingleInternalStoreTransferSuccess = (
  data: IInternalStoreTransfer,
) => action(FETCH_SINGLE_INTERNAL_STORE_TRANSFER_SUCCESS, { data });
export const fetchSingleInternalStoreTransferFailed = (errorMessage: string) =>
  action(FETCH_SINGLE_INTERNAL_STORE_TRANSFER_FAILED, { errorMessage });

export const fetchSingleInternalStoreTransferAsync =
  (
    uuid: string,
    billing_company_uuid: string,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchSingleInternalStoreTransferProgress());
      let url = `${server_base_endpoints.grn.get_internal_store_transfer}?uuid=${uuid}`;
      if (billing_company_uuid) {
        url += `&billing_company_uuid=${billing_company_uuid}`;
      }
      const res = await api.get(url);
      const data: IInternalStoreTransfer[] = res.data.data;
      if (data.length > 0) {
        dispatch(fetchSingleInternalStoreTransferSuccess(data[0]));
      } else {
        dispatch(
          fetchSingleInternalStoreTransferFailed(
            "Oops! We couldn't find any records.",
          ),
        );
      }
    } catch (err: any) {
      dispatch(fetchSingleInternalStoreTransferFailed("Something went wrong!"));
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message ||
            "Error fetching internal store transfer",
          displayAs: "snackbar",
        }),
      );
    }
  };

export const CLEAR_SINGLE_INTERNAL_STORE_TRANSFER =
  "CLEAR_SINGLE_INTERNAL_STORE_TRANSFER";
export const CLEAR_INTERNAL_STORE_TRANSFER_STATE =
  "CLEAR_INTERNAL_STORE_TRANSFER_STATE";

export const clearSingleInternalStoreTransfer = () =>
  action(CLEAR_SINGLE_INTERNAL_STORE_TRANSFER);
export const clearInternalStoreTransferState = () =>
  action(CLEAR_INTERNAL_STORE_TRANSFER_STATE);
